import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  X, 
  Home, 
  Calendar, 
  MapPin, 
  ShieldCheck, 
  Users, 
  Heart, 
  Activity, 
  Smartphone,
  ExternalLink,
  Download,
  CheckCircle2
} from 'lucide-react';
import QRCode from 'react-qr-code';
import api from '../../api/api';
import './AppDownloadPromo.css';

const DEFAULT_PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.musb.diagnostics&hl=en_IN';
const DEFAULT_APP_STORE_URL = 'https://apps.apple.com/in/app/musb-diagnostics/id6797962528';
const STORAGE_LAST_SHOWN = 'musb_app_promo_last_shown_time';
const STORAGE_DISMISSED = 'musb_app_promo_dismissed_time';
const STORAGE_DOWNLOADED = 'musb_app_downloaded';

const AppDownloadPromo = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showMiniPop, setShowMiniPop] = useState(false);
  const [config, setConfig] = useState({
    is_active: true,
    auto_show_delay_seconds: 7,
    dismiss_cooldown_hours: 24,
    suppress_if_downloaded: true,
    device_target: 'all',
    play_store_url: DEFAULT_PLAY_STORE_URL,
    app_store_url: DEFAULT_APP_STORE_URL,
    android_qr_mode: 'auto',
    android_qr_image: '',
    ios_qr_mode: 'auto',
    ios_qr_image: '',
    qr_tracking_url: '',
    android_qr_tracking_url: '',
    ios_qr_tracking_url: ''
  });
  const [activePlatformTab, setActivePlatformTab] = useState('ios');
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const configLoadedRef = useRef(false);
  const location = useLocation();

  // Check initial download status from storage
  useEffect(() => {
    try {
      const downloaded = localStorage.getItem(STORAGE_DOWNLOADED) === 'true';
      setHasDownloaded(downloaded);
    } catch (e) {
      setHasDownloaded(false);
    }
  }, []);

  // Safe tracking dispatcher
  const trackPromoEvent = useCallback((eventType) => {
    try {
      const isMobile = window.innerWidth <= 900 || /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
      const userObj = localStorage.getItem('patient_user') || localStorage.getItem('user');
      let userId = '';
      if (userObj) {
        try {
          userId = JSON.parse(userObj).user_id || JSON.parse(userObj).email || '';
        } catch (e) {}
      }
      api.post('/api/home/app-promo/track/', {
        event_type: eventType,
        device: isMobile ? 'mobile' : 'desktop',
        user_id: userId
      }).catch(() => {});
    } catch (e) {
      // Telemetry should never block UI
    }
  }, []);

  // Open full modal
  const openModal = useCallback((isManual = false) => {
    setIsOpen(true);
    setShowMiniPop(false);
    try {
      localStorage.setItem(STORAGE_LAST_SHOWN, Date.now().toString());
    } catch (e) {}
    trackPromoEvent('impression');
  }, [trackPromoEvent]);

  // Close full modal
  const closeModal = useCallback(() => {
    setIsOpen(false);
    try {
      localStorage.setItem(STORAGE_DISMISSED, Date.now().toString());
      localStorage.setItem(STORAGE_LAST_SHOWN, Date.now().toString());
    } catch (e) {}
    trackPromoEvent('dismiss');
  }, [trackPromoEvent]);

  // Handle Download Click (Google Play or Apple App Store Button)
  const handleDownloadClick = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_DOWNLOADED, 'true');
      localStorage.setItem(`${STORAGE_DOWNLOADED}_at`, Date.now().toString());
      setHasDownloaded(true);
    } catch (e) {}
    trackPromoEvent('download_click');
  }, [trackPromoEvent]);

  // Handle QR Scan Interaction
  const handleQrInteraction = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_DOWNLOADED, 'true');
      setHasDownloaded(true);
    } catch (e) {}
    trackPromoEvent('qr_scan');
  }, [trackPromoEvent]);

  // Fetch dynamic configuration from backend
  useEffect(() => {
    let isMounted = true;
    api.get('/api/home/app-promo/config/')
      .then(res => {
        if (isMounted && res.data) {
          setConfig(prev => ({
            ...prev,
            ...res.data,
            play_store_url: res.data.play_store_url || DEFAULT_PLAY_STORE_URL,
            app_store_url: res.data.app_store_url || DEFAULT_APP_STORE_URL
          }));
          configLoadedRef.current = true;
        }
      })
      .catch(() => {
        configLoadedRef.current = true;
      });
    return () => { isMounted = false; };
  }, []);

  // Intelligent schedule and auto-show logic
  useEffect(() => {
    // 0. Never auto-popup on Admin or Internal Portal routes!
    const pathname = location.pathname || '';
    const isAdminOrPortal = pathname.startsWith('/superadmin') || 
                            pathname.startsWith('/portal') || 
                            pathname.startsWith('/dispatch') ||
                            pathname.startsWith('/staff') ||
                            pathname.startsWith('/admin-invite');

    // 1. Manual test override via query string (?app_download=1 or ?download_app=1)
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('app_download') || urlParams.has('download_app') || urlParams.has('app_promo')) {
      openModal(true);
      return;
    }

    // Do NOT auto-show on admin or portal routes
    if (isAdminOrPortal) {
      return;
    }

    // 2. Check if user is already browsing inside Standalone App / PWA wrapper
    const isStandalone = 
      (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
      window.navigator.standalone === true ||
      document.referrer.includes('android-app://');
    if (isStandalone) {
      return; // Never interrupt users who are already using the installed mobile app!
    }

    // 3. Check if disabled by Super Admin
    if (config.is_active === false) {
      return;
    }

    // 4. Device targeting check
    const isMobile = window.innerWidth <= 900 || /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
    if (config.device_target === 'mobile_only' && !isMobile) return;
    if (config.device_target === 'desktop_only' && isMobile) return;

    // 5. Downloader suppression check
    let userDownloaded = false;
    try {
      userDownloaded = localStorage.getItem(STORAGE_DOWNLOADED) === 'true';
    } catch (e) {}
    if (userDownloaded && config.suppress_if_downloaded) {
      return; // User already downloaded; suppress intrusive modal completely
    }

    // 6. Dismissal Cooldown check
    let lastDismissed = 0;
    try {
      lastDismissed = Number(localStorage.getItem(STORAGE_DISMISSED) || 0);
    } catch (e) {}
    const cooldownMs = (config.dismiss_cooldown_hours || 24) * 60 * 60 * 1000;
    if (lastDismissed && (Date.now() - lastDismissed) < cooldownMs) {
      return; // Inside dismissal cooldown period
    }

    // 7. Schedule modal appearance after configured delay (default 7s)
    const delayMs = Math.max(2, config.auto_show_delay_seconds || 7) * 1000;
    const timer = setTimeout(() => {
      openModal(false);
    }, delayMs);

    return () => clearTimeout(timer);
  }, [config, openModal, location.pathname]);

  // Keyboard Escape & Scroll Lock
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        closeModal();
      }
    };

    if (isOpen) {
      document.body.classList.add('app-promo-scroll-lock');
      document.documentElement.classList.add('app-promo-scroll-lock');
      window.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.classList.remove('app-promo-scroll-lock');
      document.documentElement.classList.remove('app-promo-scroll-lock');
    }

    return () => {
      document.body.classList.remove('app-promo-scroll-lock');
      document.documentElement.classList.remove('app-promo-scroll-lock');
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, closeModal]);

  // Listen to manual open events (e.g. from navbar "Download App" button or Admin Live Preview)
  useEffect(() => {
    const handleGlobalTrigger = (e) => {
      // If preview settings provided from Admin, apply them immediately for live testing
      if (e?.detail && typeof e.detail === 'object') {
        setConfig(prev => ({
          ...prev,
          ...e.detail
        }));
        if (e.detail.app_store_url && !e.detail.play_store_url) {
          setActivePlatformTab('ios');
        }
      }
      openModal(true);
    };
    window.addEventListener('musb-open-app-promo', handleGlobalTrigger);
    return () => window.removeEventListener('musb-open-app-promo', handleGlobalTrigger);
  }, [openModal]);

  const activePlayStoreUrl = config.play_store_url || DEFAULT_PLAY_STORE_URL;
  const activeAppStoreUrl = config.app_store_url || DEFAULT_APP_STORE_URL;

  // Dual platform QR code calculations
  const isIosActive = activePlatformTab === 'ios';

  const androidQrValue = config.android_qr_tracking_url || 
    (config.qr_tracking_url ? `${config.qr_tracking_url}?platform=android` : `${window.location.origin}/api/home/app-promo/qr-redirect/?platform=android`);

  const iosQrValue = config.ios_qr_tracking_url || 
    (config.qr_tracking_url ? `${config.qr_tracking_url}?platform=ios` : `${window.location.origin}/api/home/app-promo/qr-redirect/?platform=ios`);

  const activeQrValue = isIosActive ? iosQrValue : androidQrValue;
  const activeCustomQrImage = isIosActive ? config.ios_qr_image : config.android_qr_image;
  const activeQrMode = isIosActive ? config.ios_qr_mode : config.android_qr_mode;
  const hasCustomImage = Boolean(activeCustomQrImage) && (activeQrMode === 'custom' || Boolean(activeCustomQrImage));

  return (
    <>
      {/* 1. FULLSCREEN PROMO MODAL */}
      {isOpen && (
        <div 
          className="app-promo-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
          role="dialog"
          aria-modal="true"
          aria-label="MusB Diagnostics Mobile App"
        >
          <div className="app-promo-modal-card">
            {/* Top Close Button */}
            <button 
              type="button" 
              className="app-promo-close-btn"
              onClick={closeModal}
              aria-label="Close promotional modal"
              title="Close"
            >
              <X size={20} />
            </button>

            {/* Header Bar */}
            <header className="app-promo-topbar">
              <div className="app-promo-brand">
                <img 
                  src="/images/MusB_Diagnostic_Logo.webp" 
                  alt="MusB Diagnostics Logo"
                  className="app-promo-logo-img"
                  onError={(e) => {
                    e.currentTarget.src = '/images/MusB_Diagnostic_Logo.png';
                  }}
                />
              </div>

              {/* Navigation Bar in Modal Header */}
              <nav className="app-promo-nav-menu">
                <span className="app-promo-nav-link">Home</span>
                <span className="app-promo-nav-link">About</span>
                <span className="app-promo-nav-link">Services</span>
                <span className="app-promo-nav-link active">Download App</span>
                <span className="app-promo-nav-link">Contact</span>
              </nav>

              <div className="app-promo-top-motto">
                <Heart size={15} className="app-promo-heart-icon" />
                <span>Better Health, Brighter Tomorrow</span>
              </div>
            </header>

            {/* Main Content Body */}
            <div className="app-promo-content-grid">
              
              {/* LEFT COLUMN: App Details & Store Download */}
              <div className="app-promo-left-col">
                <div className="app-promo-tag">
                  <span>OFFICIAL MOBILE APP</span>
                </div>

                <h2 className="app-promo-title">
                  MusB <span className="app-promo-title-highlight">Diagnostics</span>
                </h2>

                <h3 className="app-promo-tagline">
                  Simple. Convenient. Trusted.
                </h3>

                <p className="app-promo-description">
                  Book diagnostic tests, manage appointments, get home sample collection or visit our centres — all from your phone.
                </p>

                {/* Motive Box */}
                <div className="app-promo-motive-pill">
                  <div className="app-promo-motive-icon-wrap">
                    <Home size={18} />
                  </div>
                  <div className="app-promo-motive-text">
                    <span className="app-promo-motive-label">Our Motive:</span>
                    <span className="app-promo-motive-val">Phlebotomist is your doorstep</span>
                  </div>
                </div>

                {/* Mobile-Only Dual Phone Mockups */}
                <div className="app-promo-phones-mobile">
                  <div className="app-promo-phones-container">
                    <img 
                      src="/images/musb_app_phones.png?v=5" 
                      alt="MusB Mobile App Interface" 
                      className="app-promo-banner-photo"
                    />
                  </div>
                </div>

                {/* Mobile Direct Download Action */}
                <div className="app-promo-mobile-direct-action">
                  {/* Show App Store if configured */}
                  {activeAppStoreUrl && (
                    <a 
                      href={activeAppStoreUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="app-promo-mobile-install-btn appstore"
                      onClick={handleDownloadClick}
                      title="Download on Apple App Store"
                    >
                      <Download size={18} />
                      <span>Download on App Store</span>
                      <ExternalLink size={15} />
                    </a>
                  )}
                  {/* Show Google Play if configured */}
                  {activePlayStoreUrl && (
                    <a 
                      href={activePlayStoreUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="app-promo-mobile-install-btn playstore"
                      onClick={handleDownloadClick}
                      title="Install MusB App on Google Play"
                    >
                      <Download size={18} />
                      <span>Install on Google Play</span>
                      <ExternalLink size={15} />
                    </a>
                  )}
                  {hasDownloaded && (
                    <div className="app-promo-downloaded-badge">
                      <CheckCircle2 size={14} />
                      <span>App already downloaded</span>
                    </div>
                  )}
                </div>

                {/* Platform Selector Tabs */}
                {activeAppStoreUrl && (
                  <div className="app-promo-platform-tabs" role="tablist">
                    <button 
                      type="button" 
                      className={`app-promo-platform-tab ${isIosActive ? 'active ios' : ''}`}
                      onClick={() => setActivePlatformTab('ios')}
                      role="tab"
                      aria-selected={isIosActive}
                    >
                      <span className="app-promo-tab-dot ios" />
                      <svg viewBox="0 0 170 170" width="13" height="13" fill="currentColor">
                        <path d="M150.37 130.25c-2.45 5.66-5.35 10.87-8.71 15.66-4.58 6.53-8.33 11.05-11.22 13.56-4.48 4.12-9.28 6.23-14.42 6.35-3.69 0-8.14-1.05-13.32-3.18-5.19-2.12-9.97-3.17-14.34-3.17-4.58 0-9.49 1.05-14.75 3.17-5.26 2.13-9.5 3.24-12.74 3.35-4.35.13-9.16-1.9-14.42-6.08-3.7-3.04-7.58-7.7-11.64-13.98-5.87-9.01-10.45-19.16-13.74-30.45-3.29-11.29-4.94-22.18-4.94-32.66 0-14.54 3.76-26.68 11.27-36.43 7.51-9.75 16.99-14.77 28.43-15.06 4.78 0 10.09 1.25 15.93 3.75 5.84 2.5 9.87 3.86 12.09 4.09 1.94-.34 6.13-1.84 12.57-4.51 6.44-2.67 12.09-3.77 16.94-3.32 12.4.92 22.36 5.58 29.89 13.98-10.74 6.5-16.02 15.48-15.84 26.94.19 8.94 3.65 16.43 10.38 22.47 6.73 6.04 14.88 9.54 24.45 10.51-2.45 7.42-5.46 14.78-9.03 22.08zM119.22 33.61c0-7.39 2.65-14.15 7.95-20.28 5.3-6.13 11.83-9.98 19.59-11.55.22 1.3.33 2.45.33 3.45 0 7.28-2.73 14.16-8.19 20.64-5.46 6.48-12.18 10.37-20.16 11.67-.32-1.3-.48-2.61-.48-3.93z"/>
                      </svg>
                      <span>iOS (App Store)</span>
                    </button>
                    <button 
                      type="button" 
                      className={`app-promo-platform-tab ${!isIosActive ? 'active android' : ''}`}
                      onClick={() => setActivePlatformTab('android')}
                      role="tab"
                      aria-selected={!isIosActive}
                    >
                      <span className="app-promo-tab-dot android" />
                      <Smartphone size={14} />
                      <span>Android (Play Store)</span>
                    </button>
                  </div>
                )}

                {/* QR Code & Direct Action (Desktop & Tablet) */}
                <div className="app-promo-download-block">
                  <div className="app-promo-qr-column">
                    {/* Explicit Platform Mention Badge */}
                    <div className={`app-promo-qr-platform-badge ${isIosActive ? 'ios' : 'android'}`}>
                      {isIosActive ? (
                        <>
                          <svg viewBox="0 0 170 170" width="12" height="12" fill="currentColor">
                            <path d="M150.37 130.25c-2.45 5.66-5.35 10.87-8.71 15.66-4.58 6.53-8.33 11.05-11.22 13.56-4.48 4.12-9.28 6.23-14.42 6.35-3.69 0-8.14-1.05-13.32-3.18-5.19-2.12-9.97-3.17-14.34-3.17-4.58 0-9.49 1.05-14.75 3.17-5.26 2.13-9.5 3.24-12.74 3.35-4.35.13-9.16-1.9-14.42-6.08-3.7-3.04-7.58-7.7-11.64-13.98-5.87-9.01-10.45-19.16-13.74-30.45-3.29-11.29-4.94-22.18-4.94-32.66 0-14.54 3.76-26.68 11.27-36.43 7.51-9.75 16.99-14.77 28.43-15.06 4.78 0 10.09 1.25 15.93 3.75 5.84 2.5 9.87 3.86 12.09 4.09 1.94-.34 6.13-1.84 12.57-4.51 6.44-2.67 12.09-3.77 16.94-3.32 12.4.92 22.36 5.58 29.89 13.98-10.74 6.5-16.02 15.48-15.84 26.94.19 8.94 3.65 16.43 10.38 22.47 6.73 6.04 14.88 9.54 24.45 10.51-2.45 7.42-5.46 14.78-9.03 22.08zM119.22 33.61c0-7.39 2.65-14.15 7.95-20.28 5.3-6.13 11.83-9.98 19.59-11.55.22 1.3.33 2.45.33 3.45 0 7.28-2.73 14.16-8.19 20.64-5.46 6.48-12.18 10.37-20.16 11.67-.32-1.3-.48-2.61-.48-3.93z"/>
                          </svg>
                          <span>FOR iOS</span>
                        </>
                      ) : (
                        <>
                          <span className="app-promo-badge-dot android" />
                          <span>FOR ANDROID</span>
                        </>
                      )}
                    </div>

                    {/* QR Code Container */}
                    <div 
                      className="app-promo-qr-card" 
                      onClick={() => {
                        handleQrInteraction();
                        const url = activeQrValue;
                        window.open(url, '_blank', 'noopener,noreferrer');
                      }} 
                      title={`Scan with phone camera to download for ${isIosActive ? 'iOS' : 'Android'} or click to open`}
                      style={{ cursor: 'pointer' }}
                    >
                      {hasCustomImage ? (
                        <img 
                          src={activeCustomQrImage} 
                          alt={`MusB Diagnostics ${isIosActive ? 'iOS' : 'Android'} QR Code`} 
                          className="app-promo-qr-img"
                        />
                      ) : (
                        <QRCode 
                          value={activeQrValue}
                          size={98}
                          style={{ height: 'auto', maxWidth: '100%', width: '100%', display: 'block' }}
                          viewBox="0 0 256 256"
                        />
                      )}
                    </div>

                    <span className="app-promo-qr-subtext">
                      {isIosActive ? 'Scan with iPhone' : 'Scan with Android'}
                    </span>
                  </div>

                  <div className="app-promo-download-actions">
                    <p className="app-promo-scan-instruction">
                      Scan the QR code to download the <strong>MusB Diagnostics App</strong>
                    </p>

                    <div className="app-promo-or-divider">
                      <span>OR DIRECT DOWNLOAD</span>
                    </div>

                    {/* Dual Store Badges */}
                    <div className="app-promo-store-buttons-wrap">
                      {/* Apple App Store Button */}
                      {activeAppStoreUrl && (
                        <a 
                          href={activeAppStoreUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`app-promo-store-btn appstore ${isIosActive ? 'highlighted' : ''}`}
                          onClick={handleDownloadClick}
                          onMouseEnter={() => setActivePlatformTab('ios')}
                          title="Download on Apple App Store"
                        >
                          <svg viewBox="0 0 170 170" className="app-promo-apple-svg" fill="currentColor">
                            <path d="M150.37 130.25c-2.45 5.66-5.35 10.87-8.71 15.66-4.58 6.53-8.33 11.05-11.22 13.56-4.48 4.12-9.28 6.23-14.42 6.35-3.69 0-8.14-1.05-13.32-3.18-5.19-2.12-9.97-3.17-14.34-3.17-4.58 0-9.49 1.05-14.75 3.17-5.26 2.13-9.5 3.24-12.74 3.35-4.35.13-9.16-1.9-14.42-6.08-3.7-3.04-7.58-7.7-11.64-13.98-5.87-9.01-10.45-19.16-13.74-30.45-3.29-11.29-4.94-22.18-4.94-32.66 0-14.54 3.76-26.68 11.27-36.43 7.51-9.75 16.99-14.77 28.43-15.06 4.78 0 10.09 1.25 15.93 3.75 5.84 2.5 9.87 3.86 12.09 4.09 1.94-.34 6.13-1.84 12.57-4.51 6.44-2.67 12.09-3.77 16.94-3.32 12.4.92 22.36 5.58 29.89 13.98-10.74 6.5-16.02 15.48-15.84 26.94.19 8.94 3.65 16.43 10.38 22.47 6.73 6.04 14.88 9.54 24.45 10.51-2.45 7.42-5.46 14.78-9.03 22.08zM119.22 33.61c0-7.39 2.65-14.15 7.95-20.28 5.3-6.13 11.83-9.98 19.59-11.55.22 1.3.33 2.45.33 3.45 0 7.28-2.73 14.16-8.19 20.64-5.46 6.48-12.18 10.37-20.16 11.67-.32-1.3-.48-2.61-.48-3.93z"/>
                          </svg>
                          <div className="app-promo-store-text">
                            <span className="app-promo-store-small">Download on the</span>
                            <span className="app-promo-store-large">App Store</span>
                          </div>
                        </a>
                      )}

                      {/* Google Play Button */}
                      {activePlayStoreUrl && (
                        <a 
                          href={activePlayStoreUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`app-promo-store-btn playstore ${!isIosActive ? 'highlighted' : ''}`}
                          onClick={handleDownloadClick}
                          onMouseEnter={() => setActivePlatformTab('android')}
                          title="Download on Google Play"
                        >
                          <svg viewBox="0 0 24 24" className="app-promo-play-svg" fill="currentColor">
                            <path d="M3.609 1.814L13.792 12 3.61 22.186a2.03 2.03 0 0 1-.22-.964V2.778c0-.36.08-.696.22-.964z" fill="#00D2FF" />
                            <path d="M17.436 8.356L14.77 11.02 3.609 1.814a2.07 2.07 0 0 1 1.62-.27l12.207 6.812z" fill="#00E676" />
                            <path d="M17.436 15.644L5.229 22.456c-.53.296-1.13.364-1.62.27l11.16-10.206 2.667 3.124z" fill="#FF3D00" />
                            <path d="M21.282 10.505l-3.846-2.149-2.666 2.664 2.666 2.664 3.846-2.148c.718-.401.718-1.63 0-2.031z" fill="#FFC107" />
                          </svg>
                          <div className="app-promo-store-text">
                            <span className="app-promo-store-small">GET IT ON</span>
                            <span className="app-promo-store-large">Google Play</span>
                          </div>
                        </a>
                      )}
                    </div>

                    <div className="app-promo-platforms-note">
                      <Smartphone size={13} />
                      <span>Available for {activeAppStoreUrl ? 'Android & iOS' : 'Android'}</span>
                    </div>
                  </div>
                </div>

              </div>

              {/* CENTER COLUMN: Dual Phone Mockups */}
              <div className="app-promo-center-col">
                <div className="app-promo-phones-container">
                  <img 
                    src="/images/musb_app_phones.png?v=4" 
                    alt="MusB Mobile App Interface" 
                    className="app-promo-banner-photo"
                  />
                </div>
              </div>

              {/* RIGHT COLUMN: Feature Highlights */}
              <div className="app-promo-right-col">
                <div className="app-promo-feature-item">
                  <div className="app-promo-feature-icon-circle">
                    <Calendar size={22} />
                  </div>
                  <div className="app-promo-feature-info">
                    <h4>Book a Test</h4>
                    <p>Schedule in minutes</p>
                  </div>
                </div>

                <div className="app-promo-feature-item">
                  <div className="app-promo-feature-icon-circle">
                    <Home size={22} />
                  </div>
                  <div className="app-promo-feature-info">
                    <h4>Home Collection</h4>
                    <p>Phlebotomist at your doorstep</p>
                  </div>
                </div>

                <div className="app-promo-feature-item">
                  <div className="app-promo-feature-icon-circle">
                    <MapPin size={22} />
                  </div>
                  <div className="app-promo-feature-info">
                    <h4>Find Centres</h4>
                    <p>Locate nearby diagnostic centres</p>
                  </div>
                </div>
              </div>

            </div>

            {/* Bottom Navy Bar */}
            <footer className="app-promo-bottom-bar">
              <div className="app-promo-trust-item">
                <ShieldCheck size={20} className="app-promo-trust-icon" />
                <div className="app-promo-trust-text">
                  <strong>Trusted Care</strong>
                  <span>Your health, our priority</span>
                </div>
              </div>

              <div className="app-promo-trust-divider" />

              <div className="app-promo-trust-item">
                <Users size={20} className="app-promo-trust-icon" />
                <div className="app-promo-trust-text">
                  <strong>Accurate Results</strong>
                  <span>Reliable diagnostics</span>
                </div>
              </div>

              <div className="app-promo-trust-divider" />

              <div className="app-promo-trust-item">
                <Heart size={20} className="app-promo-trust-icon" />
                <div className="app-promo-trust-text">
                  <strong>Convenient &amp; Simple</strong>
                  <span>All in one app</span>
                </div>
              </div>

              <div className="app-promo-trust-divider" />

              <div className="app-promo-trust-item">
                <Activity size={20} className="app-promo-trust-icon" />
                <div className="app-promo-trust-text">
                  <strong>Better Health,</strong>
                  <span>Brighter Tomorrow</span>
                </div>
              </div>
            </footer>

          </div>
        </div>
      )}

      {/* 2. FLOATING BOTTOM-RIGHT LAUNCHER FOR QUICK MANUAL ACCESS */}
      {showMiniPop && !isOpen && (
        <aside 
          className="app-floating-minipop-wrap"
          onClick={() => openModal(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openModal(true);
            }
          }}
          aria-label="Open MusB Diagnostics App Download details"
          title="Download MusB Diagnostics App"
        >
          <div className="app-floating-message-pop">
            <div className="app-minipop-icon-circle">
              <Smartphone size={20} className="app-minipop-phone-icon" />
              <span className="app-minipop-pulse-dot" />
            </div>
            <div className="app-minipop-text-block">
              <span className="app-minipop-badge-label">OFFICIAL APP</span>
              <strong className="app-minipop-msg-title">Download MusB App</strong>
            </div>
            <div className="app-minipop-action-btn">
              <span>View</span>
            </div>
          </div>
        </aside>
      )}
    </>
  );
};

export default AppDownloadPromo;
